<script setup>
import { ref, computed } from 'vue';

const model = defineModel({ default: '' });
const props = defineProps({
  options: {
    type: Array,
    required: true,
    validator: (value) => value.every(
      item =>
        typeof item === 'object' &&
        typeof item.value === 'string' &&
        typeof item.label === 'string'
    )
  },

  id: {
    type: String,
    default: 'form-input'
  },
  label: {
    type: String,
  },
  placeholder: {
    type: String
  },
  inputTheme: {
    type: String,
    default: 'dark',
    validator: (theme) => ['light', 'dark'].includes(theme)
  },
  size: {
    type: String,
    default: 'md',
    validator: (size) => ['xs', 'sm', 'md', 'lg'].includes(size)
  },

  validationStatus: {
    type: String,
    validator: (type) => ['error', 'success', undefined].includes(type)
  },
  required: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const selectRef = ref(null);
const validationTransparentRef = ref(null);
const isFocused = ref(false);

const selectedOption = computed(() => model.value
  ? props.options.find(option => option.value === model.value)?.value ?? ''
  : ''
);

const selectClasses = computed(() => ({
  focused: !!isFocused.value,

  'block px-0 w-full bg-transparent border-0 border-b appearance-none focus:outline-hidden focus:ring-0 peer truncate text-ellipsis': true,

  'px-1 py-1.5 text-xs': props.size === 'xs',
  'px-1.5 py-2 text-sm': props.size === 'sm',
  'px-2 py-2.5 text-base': props.size === 'md',
  'px-2.5 py-2.5 text-lg': props.size === 'lg',

  'disabled text-gray-400': props.disabled,
  'dark-theme text-gray-900': !props.disabled && props.inputTheme === 'dark',
  'light-theme text-white': !props.disabled && props.inputTheme === 'light',

  'border-gray-400': props.disabled,
  'border-gray-900 focus:border-black': !props.disabled && props.inputTheme === 'dark' && !props.validationStatus,
  'border-white/25 focus:border-white': !props.disabled && props.inputTheme === 'light' && !props.validationStatus,

  'border-red-600': props.validationStatus === 'error',
  'border-green-600': props.validationStatus === 'success',

  'text-transparent!': validationTransparentRef.value && props.validationStatus && !isFocused.value
}));

const labelClasses = computed(() => ({
  'absolute duration-300 transform left-0 -z-10 origin-[0] peer-focus:start-0 peer-focus:rtl:translate-x-1/4 peer-focus:rtl:left-auto capitalize truncate text-ellipsis text-start': true,

  'top-1.5 text-xs scale-[.80] peer-focus:scale-[.80] -translate-y-4 peer-focus:-translate-y-4': props.size === 'xs',
  'top-2 text-sm scale-75 peer-focus:scale-75 -translate-y-5 peer-focus:-translate-y-5': props.size === 'sm',
  'top-3 text-base scale-[.65] peer-focus:scale-[.65] -translate-y-6 peer-focus:-translate-y-6': props.size === 'md',
  'top-3 text-lg scale-[.55] peer-focus:scale-[.55] -translate-y-6 peer-focus:-translate-y-6': props.size === 'lg',

  'w-full scale-100! translate-y-0! pr-6': !selectedOption.value,

  'text-gray-400': props.disabled,
  'text-gray-500 peer-focus:text-black': !props.disabled && props.inputTheme === 'dark' && !props.validationStatus,
  'text-white peer-focus:text-white': !props.disabled && props.inputTheme === 'light' && !props.validationStatus,

  'text-red-600': !props.disabled && props.validationStatus === 'error',
  'text-green-600': !props.disabled && props.validationStatus === 'success',

  'hidden!': validationTransparentRef.value && props.validationStatus && !isFocused.value
}));

const placeholderClasses = computed(() => ({
  hidden: true,

  'text-gray-400': props.disabled,
  'text-gray-900': !props.disabled && props.inputTheme === 'dark',
  'text-white': !props.disabled && props.inputTheme === 'light',

  'text-transparent!': validationTransparentRef.value && props.validationStatus && !isFocused.value
}));

const validationWrapperClasses = computed(() => ({
  'mt-1 text-xs': props.size === 'xs',
  'mt-1.5 text-sm': props.size === 'sm',
  'mt-2 text-base': props.size === 'md',
  'mt-2.5 text-lg': props.size === 'lg',

  'text-green-600': props.validationStatus === 'success',
  'text-red-600': props.validationStatus === 'error'
}));

const validationTransparentClasses = computed(() => ({
  'absolute top-0 left-0 right-0 bottom-0.5 flex items-center justify-center': true,

  // This is used to hide the select arrow when validation message is shown
  'validation-transparent': !isFocused.value,
  hidden: isFocused.value,

  'text-[10px]/[10px]': props.size === 'xs',
  'text-xs': props.size === 'sm',
  'text-sm': props.size === 'md',
  'text-base': props.size === 'lg',

  'bg-green-600/25 text-white': props.validationStatus === 'success',
  'bg-red-600/25 text-white animated shake': props.validationStatus === 'error'
}));

function onFocus (e) {
  isFocused.value = true;
}

function onBlur (e) {
  isFocused.value = false;
}

function focus () {
  if (selectRef.value) {
    selectRef.value.focus();
  }
}
</script>

<template>
  <div>
    <div class="form-select-wrapper relative z-0">
      <select
        ref="selectRef"
        :id="id"
        :class="selectClasses"
        :disabled="disabled"
        :required="required"
        @change="model = $event.target.value"
        @focus="onFocus"
        @blur="onBlur"
      >
        <option
          :class="placeholderClasses"
          :selected="!selectedOption"
        >
          {{ required && !label ? '* ' : '' }}{{ placeholder }}
        </option>

        <option
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :selected="option.value === selectedOption"
          class="text-gray-900"
        >
          {{ option.label }}
        </option>
      </select>
      <label
        v-if="label"
        :for="id"
        :class="labelClasses"
      >
        {{ required ? '* ' : '' }}{{ $t(label) }}
      </label>

      <p
        v-if="$slots.validationTransparent"
        ref="validationTransparentRef"
        :class="validationTransparentClasses"
        @click="focus"
      >
        <slot name="validationTransparent" />
      </p>
    </div>

    <p
      v-if="$slots.validationMessage"
      :class="validationWrapperClasses"
    >
      <slot name="validationMessage" />
    </p>
  </div>
</template>

<style lang="scss" scoped>
@reference '#tailwind.scss';

.form-select-wrapper:has(> .validation-transparent):not(.hidden) > select {
  background-image: none !important;
}

select {
  print-color-adjust: exact;
  background-position: right .5rem center;
  background-repeat: no-repeat;
  background-size: 1em 1em;
  padding-right: 2.5rem;

  &.disabled {
    background-image: url("data:image/svg+xml,%3csvg aria-hidden='true' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 10 6'%3e %3cpath stroke='#9CA3AF' stroke-linecap='round' stroke-linejoin='round' stroke-width='1' d='m1 1 4 4 4-4'/%3e %3c/svg%3e");
  }

  &.light-theme {
    background-image: url("data:image/svg+xml,%3csvg aria-hidden='true' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 10 6'%3e %3cpath stroke='#FFFFFF' stroke-linecap='round' stroke-linejoin='round' stroke-width='1' d='m1 1 4 4 4-4'/%3e %3c/svg%3e");
  }

  &.dark-theme {
    background-image: url("data:image/svg+xml,%3csvg aria-hidden='true' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 10 6'%3e %3cpath stroke='#000000' stroke-linecap='round' stroke-linejoin='round' stroke-width='1' d='m1 1 4 4 4-4'/%3e %3c/svg%3e");
  }
}
</style>
